"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
} from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  IRetryPolicy,
  LogLevel,
} from "@microsoft/signalr";
import {
  SignalRContextValue,
  SignalRState,
  SignalRProviderProps,
  ConnectionState,
  SignalRConnectionOptions,
} from "./types";

type SignalRAction =
  | { type: "SET_CONNECTION"; payload: HubConnection | null }
  | { type: "SET_CONNECTION_STATE"; payload: ConnectionState }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_LAST_MESSAGE"; payload: unknown }
  | { type: "INCREMENT_RECONNECT_ATTEMPTS" }
  | { type: "RESET_RECONNECT_ATTEMPTS" };

const initialState: SignalRState = {
  connection: null,
  connectionState: "Disconnected",
  isConnected: false,
  error: null,
  lastMessage: null,
  reconnectAttempts: 0,
};

function signalRReducer(
  state: SignalRState,
  action: SignalRAction
): SignalRState {
  switch (action.type) {
    case "SET_CONNECTION":
      return {
        ...state,
        connection: action.payload,
        isConnected: action.payload?.state === "Connected",
      };
    case "SET_CONNECTION_STATE":
      return {
        ...state,
        connectionState: action.payload,
        isConnected: action.payload === "Connected",
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
      };
    case "SET_LAST_MESSAGE":
      return {
        ...state,
        lastMessage: action.payload,
      };
    case "INCREMENT_RECONNECT_ATTEMPTS":
      return {
        ...state,
        reconnectAttempts: state.reconnectAttempts + 1,
      };
    case "RESET_RECONNECT_ATTEMPTS":
      return {
        ...state,
        reconnectAttempts: 0,
      };
    default:
      return state;
  }
}

const SignalRContext = createContext<SignalRContextValue | null>(null);

export function SignalRProvider({
  children,
  autoConnect = false,
  connectionUrl,
  connectionOptions,
}: SignalRProviderProps) {
  const [state, dispatch] = useReducer(signalRReducer, initialState);

  const connect = useCallback(
    async (url: string, options?: SignalRConnectionOptions) => {
      try {
        dispatch({ type: "SET_CONNECTION_STATE", payload: "Connecting" });
        dispatch({ type: "SET_ERROR", payload: null });

        const connectionBuilder = new HubConnectionBuilder()
          .withUrl(url, {
            withCredentials: options?.withCredentials,
            headers: options?.headers,
            accessTokenFactory: options?.accessTokenFactory,
            transport: options?.transport,
            skipNegotiation: options?.skipNegotiation,
          })
          .withAutomaticReconnect(
            options?.automaticReconnect
              ? ([0, 1000, 5000, 10000] as unknown as IRetryPolicy)
              : (undefined as unknown as IRetryPolicy)
          )
          .configureLogging(
            process.env.NODE_ENV === "development"
              ? LogLevel.Information
              : LogLevel.Error
          );

        const connection = connectionBuilder.build();

        // Set up connection event handlers
        connection.onclose((error?: Error) => {
          dispatch({ type: "SET_CONNECTION_STATE", payload: "Disconnected" });
          if (error) {
            dispatch({ type: "SET_ERROR", payload: error.message });
          }
        });

        connection.onreconnecting((error?: Error) => {
          dispatch({ type: "SET_CONNECTION_STATE", payload: "Reconnecting" });
          dispatch({ type: "INCREMENT_RECONNECT_ATTEMPTS" });
          if (error) {
            dispatch({ type: "SET_ERROR", payload: error.message });
          }
        });

        connection.onreconnected(() => {
          dispatch({ type: "SET_CONNECTION_STATE", payload: "Connected" });
          dispatch({ type: "RESET_RECONNECT_ATTEMPTS" });
          dispatch({ type: "SET_ERROR", payload: null });
        });

        await connection.start();

        dispatch({ type: "SET_CONNECTION", payload: connection });
        dispatch({ type: "SET_CONNECTION_STATE", payload: "Connected" });
        dispatch({ type: "RESET_RECONNECT_ATTEMPTS" });
      } catch (error) {
        dispatch({ type: "SET_CONNECTION_STATE", payload: "Disconnected" });
        dispatch({
          type: "SET_ERROR",
          payload: error instanceof Error ? error.message : "Connection failed",
        });
      }
    },
    []
  );

  const disconnect = useCallback(async () => {
    if (state.connection) {
      try {
        dispatch({ type: "SET_CONNECTION_STATE", payload: "Disconnecting" });
        await state.connection.stop();
        dispatch({ type: "SET_CONNECTION", payload: null });
        dispatch({ type: "SET_CONNECTION_STATE", payload: "Disconnected" });
      } catch (error) {
        dispatch({
          type: "SET_ERROR",
          payload:
            error instanceof Error ? error.message : "Disconnection failed",
        });
      }
    }
  }, [state.connection]);

  const sendMessage = useCallback(
    async (methodName: string, ...args: unknown[]) => {
      if (!state.connection || !state.isConnected) {
        throw new Error("SignalR connection is not established");
      }

      try {
        await state.connection.invoke(methodName, ...args);
      } catch (error) {
        dispatch({
          type: "SET_ERROR",
          payload:
            error instanceof Error ? error.message : "Send message failed",
        });
        throw error;
      }
    },
    [state.connection, state.isConnected]
  );

  const onMessage = useCallback(
    (methodName: string, callback: (...args: unknown[]) => void) => {
      if (state.connection) {
        state.connection.on(methodName, (...args: unknown[]) => {
          dispatch({
            type: "SET_LAST_MESSAGE",
            payload: { method: methodName, args },
          });
          callback(...args);
        });
      }
    },
    [state.connection]
  );

  const offMessage = useCallback(
    (methodName: string, callback?: (...args: unknown[]) => void) => {
      if (state.connection) {
        if (callback) {
          state.connection.off(methodName, callback);
        } else {
          state.connection.off(methodName);
        }
      }
    },
    [state.connection]
  );

  const clearError = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });
  }, []);

  const reconnect = useCallback(async () => {
    if (state.connection) {
      try {
        await state.connection.start();
      } catch (error) {
        dispatch({
          type: "SET_ERROR",
          payload:
            error instanceof Error ? error.message : "Reconnection failed",
        });
      }
    }
  }, [state.connection]);

  // Auto-connect after idle so first paint and RSC hydration are less contended with negotiation/WebSockets
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!autoConnect || !connectionUrl || state.connection) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) {
        void connect(connectionUrl, connectionOptions);
      }
    };

    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(id);
      };
    }

    const t = globalThis.setTimeout(run, 1);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(t);
    };
  }, [
    autoConnect,
    connectionUrl,
    connectionOptions,
    connect,
    state.connection,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (state.connection) {
        state.connection.stop();
      }
    };
  }, [state.connection]);

  const contextValue: SignalRContextValue = {
    ...state,
    connect,
    disconnect,
    sendMessage,
    onMessage,
    offMessage,
    clearError,
    reconnect,
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalRContext(): SignalRContextValue {
  const context = useContext(SignalRContext);
  if (!context) {
    return {
      connection: null,
      connectionState: "Disconnected" as ConnectionState,
      isConnected: false,
      error: null,
      lastMessage: null,
      reconnectAttempts: 0,
      connect: async () => {},
      disconnect: async () => {},
      sendMessage: async () => {},
      onMessage: () => {},
      offMessage: () => {},
      clearError: () => {},
      reconnect: async () => {},
    };
  }
  return context;
}
